import { Box, CardMedia, Typography } from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { useEffect, useMemo, useRef, useState } from 'react';
import AudioPlayerWithSpeed from './AudioPlayerWithSpeed';
import PdfPreview from './PDFPreview';
import VisualizadorImagem from './VizualizadorImagem';
import { isAudioDisabledDevice } from '../controller/android-interface-controller';

export default function FileTypeFilter({ urlFile, tone = 'incoming', onReply = null }) {
  const [audioBlockedForDevice, setAudioBlockedForDevice] = useState(() => isAudioDisabledDevice());
  const type = useMemo(
    () => urlFile.slice(urlFile.lastIndexOf('.') + 1).toLowerCase(),
    [urlFile]
  );
  const videoRef = useRef(null);
  const isAndroidWebView =
    typeof window !== 'undefined' && typeof window.Android?.openPDF === 'function';

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 30;
    const syncDeviceAudioState = () => {
      const blocked = isAudioDisabledDevice();
      setAudioBlockedForDevice(blocked);
      if (blocked) return true;
      attempts += 1;
      return attempts >= maxAttempts;
    };

    if (syncDeviceAudioState()) return undefined;
    const timer = setInterval(() => {
      if (syncDeviceAudioState()) clearInterval(timer);
    }, 1000);

    const onFocus = () => syncDeviceAudioState();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  switch (type) {
    case 'png':
    case 'jpg':
    case 'jpeg':
      return <VisualizadorImagem arquivo={urlFile} />;

    case 'pdf':
      return <PdfPreview urlFile={urlFile} isAndroid={isAndroidWebView} />;

    case 'mp4':
    case '3gpp':
    case 'mov':
      return (
        <CardMedia
          component="video"
          height={270}
          src={urlFile}
          controls
          ref={videoRef}
        />
      );

    case 'webm':
    case 'ogg':
    case 'mp3':
    case 'wav':
    case 'm4a':
    case 'aac': {
      const isOutgoing = tone === 'outgoing';
      if (audioBlockedForDevice) {
        return (
          <Box
            sx={{
              minWidth: 0,
              width: { xs: 'min(320px, calc(100vw - 122px))', sm: '100%' },
              maxWidth: { xs: 'calc(100vw - 122px)', sm: 520 },
              overflow: 'hidden',
              p: 1.2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isOutgoing ? 'rgba(6,66,93,0.85)' : 'rgba(0,0,0,0.12)',
              background: isOutgoing
                ? 'linear-gradient(180deg, #0b7aa8 0%, #075f84 100%)'
                : 'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.03) 100%)',
              boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
              color: isOutgoing ? '#fff' : 'text.primary',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <GraphicEqIcon
                fontSize="small"
                sx={{ color: isOutgoing ? '#fff' : 'inherit' }}
              />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: isOutgoing ? '#fff' : 'inherit' }}
              >
                Audio desabilitado no dispositivo
              </Typography>
            </Box>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            minWidth: 0,
            width: { xs: 'min(320px, calc(100vw - 122px))', sm: '100%' },
            maxWidth: { xs: 'calc(100vw - 122px)', sm: 520 },
            overflow: 'hidden',
            p: 1.2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isOutgoing ? 'rgba(6,66,93,0.85)' : 'rgba(0,0,0,0.12)',
            background: isOutgoing
              ? 'linear-gradient(180deg, #0b7aa8 0%, #075f84 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.03) 100%)',
            boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
            color: isOutgoing ? '#fff' : 'text.primary',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <GraphicEqIcon
              fontSize="small"
              sx={{ color: isOutgoing ? '#fff' : 'inherit' }}
            />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: isOutgoing ? '#fff' : 'inherit' }}
            >
              Mensagem de voz
            </Typography>
          </Box>
          <AudioPlayerWithSpeed src={urlFile} tone={tone}>
            {typeof onReply === 'function' && (
              <Typography
                variant="caption"
                onClick={onReply}
                sx={{
                  cursor: 'pointer',
                  color: isOutgoing ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                }}
              >
                Responder
              </Typography>
            )}
          </AudioPlayerWithSpeed>
        </Box>
      );
    }

    default:
      return (
        <Typography color="warning">
          Erro ao renderizar arquivo ou arquivo não aceito!
        </Typography>
      );
  }
}
